/**
 * Routing — spec §F1.
 *   `/`          Map Explorer (+ splash on first load, §F4/M7)
 *   `/dashboard` §F7   `/catalogue` §F8   `/manager` §F9 (analyst+)   `/admin` §F10 (manager)
 * "Analysis" nav = `/` with the toolbox dock open; "Overview" nav = re-open the splash.
 * Route guards per §I2 — unauthorised → redirect `/` + toast (UI gating only; the server
 * is the enforcement, §I2).
 */
import { useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { MapPage } from '../features/map';
import { DashboardPage } from '../features/dashboard';
import { CataloguePage } from '../features/catalogue';
import { ManagerPage } from '../features/manager';
import { AdminPage } from '../features/admin';
import { Placeholder } from '../components/Placeholder';
import { useAppStore } from '../lib/state';
import { canAccess } from '../lib/access';
import { toast } from '../lib/toast';

function RoleGuard({ path, children }: { path: string; children: ReactNode }) {
  const role = useAppStore((s) => s.role);
  const { t } = useTranslation();
  const ok = canAccess(path, role);
  useEffect(() => {
    if (!ok) toast(t('error.forbidden'));
  }, [ok, t]);
  return ok ? <>{children}</> : <Navigate to="/" replace />;
}

export function AppRoutes() {
  const { t } = useTranslation();
  const location = useLocation();
  return (
    <div id="views">
      <Routes location={location}>
        <Route path="/" element={<MapPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/catalogue" element={<CataloguePage />} />
        <Route
          path="/manager"
          element={
            <RoleGuard path="/manager">
              <ManagerPage />
            </RoleGuard>
          }
        />
        <Route
          path="/admin"
          element={
            <RoleGuard path="/admin">
              <AdminPage />
            </RoleGuard>
          }
        />
        <Route
          path="*"
          element={
            <Placeholder
              title={t('placeholder.notFound')}
              body={t('placeholder.notFoundBody')}
              milestone="M1"
            />
          }
        />
      </Routes>
    </div>
  );
}

export default AppRoutes;
